import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { RiskBadge, EvidenceBadge } from "@/components/Badges";
import LevelBadge from "@/components/LevelBadge";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
} from "recharts";
import { CheckCircle2, XCircle, AlertTriangle, Wrench, FileWarning } from "lucide-react";

const SHORT = {
  labor_human_rights: "Labor",
  ehs_environment: "EHS",
  business_ethics_anticorruption: "Ethics",
  responsible_procurement_due_diligence: "Procurement",
  management_system_improvement: "Mgmt",
};

const Gauge = ({ value, size = 168 }) => {
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  const dash = c * pct;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF2F8" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#12935A" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-semibold tabular text-navy-900" data-testid="scorecard-overall-score">{value}</span>
        <span className="text-xs text-[var(--wechr-muted-text)]">out of 100</span>
      </div>
    </div>
  );
};

export const ScorecardView = ({ review, reviewDate, expirationDate, preliminary = false }) => {
  if (!review) return null;
  const cats = review.category_scores || [];
  const radarData = cats.map((c) => ({ subject: SHORT[c.key] || c.name, score: c.score, fullMark: 100 }));

  return (
    <div className="space-y-6" data-testid="scorecard-view">
      {preliminary && (
        <div className="flex items-start gap-2 rounded-lg border border-gold-500 bg-gold-100 p-3 text-sm text-gold-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span><strong>Preliminary AI review.</strong> This is an AI-assisted assessment only. Final certification requires admin document review and approval.</span>
        </div>
      )}

      {/* Overview */}
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Overall Assessment</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center gap-4 sm:flex-row sm:justify-around">
            <Gauge value={review.overall_score} />
            <LevelBadge level={review.certification_level} size={120} testId="scorecard-level-badge" />
          </CardContent>
          {(reviewDate || expirationDate) && (
            <CardContent className="pt-0">
              <Separator className="mb-3" />
              <div className="flex justify-between text-sm">
                {reviewDate && <div><span className="text-[var(--wechr-muted-text)]">Review date</span><div className="font-medium text-navy-900">{reviewDate}</div></div>}
                {expirationDate && <div className="text-right"><span className="text-[var(--wechr-muted-text)]">Expiration</span><div className="font-medium text-navy-900">{expirationDate}</div></div>}
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="lg:col-span-7">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Category Profile</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }} data-testid="scorecard-radar-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260} debounce={80}>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#E3E8F2" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: "#5B6B84", fontSize: 12 }} />
                  <Radar dataKey="score" stroke="#12935A" fill="#12935A" fillOpacity={0.2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gap summary */}
      {review.gap_summary && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Gap Summary</CardTitle></CardHeader>
          <CardContent><p className="text-sm leading-relaxed text-navy-800">{review.gap_summary}</p></CardContent>
        </Card>
      )}

      {/* Category cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {cats.map((c) => (
          <Card key={c.key} data-testid="scorecard-category-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">{c.name}</CardTitle>
                <span className="font-heading text-2xl font-semibold tabular text-navy-900">{c.score}</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Progress value={c.score} className="h-2" />
              <div className="flex flex-wrap gap-2">
                <EvidenceBadge level={c.evidence_level} />
                <RiskBadge rating={c.risk_rating} testId="scorecard-category-risk-badge" />
              </div>
              {c.strengths?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--wechr-muted-text)]">Strengths</p>
                  <ul className="space-y-1">
                    {c.strengths.slice(0, 4).map((s, i) => (
                      <li key={i} className="flex gap-1.5 text-sm text-navy-800"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-wgreen-600" />{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {c.gaps?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--wechr-muted-text)]">Gaps</p>
                  <ul className="space-y-1">
                    {c.gaps.slice(0, 4).map((s, i) => (
                      <li key={i} className="flex gap-1.5 text-sm text-navy-800"><XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#B42318]" />{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lists */}
      <div className="grid gap-4 md:grid-cols-2">
        {review.key_strengths?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><CheckCircle2 className="h-4 w-4 text-wgreen-600" /> Key Strengths</CardTitle></CardHeader>
            <CardContent><ul className="space-y-2">{review.key_strengths.map((s, i) => <li key={i} className="text-sm text-navy-800">• {s}</li>)}</ul></CardContent>
          </Card>
        )}
        {review.missing_evidence?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><FileWarning className="h-4 w-4 text-gold-700" /> Missing Evidence</CardTitle></CardHeader>
            <CardContent><ul className="space-y-2">{review.missing_evidence.map((s, i) => <li key={i} className="text-sm text-navy-800">• {s}</li>)}</ul></CardContent>
          </Card>
        )}
      </div>

      {review.red_flags?.length > 0 && (
        <Card className="border-[rgba(180,35,24,0.3)]">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#B42318]"><AlertTriangle className="h-4 w-4" /> Red Flags</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {review.red_flags.map((rf, i) => (
              <div key={i} className="flex flex-col gap-1 rounded-lg bg-[#FEF3F2] p-3 sm:flex-row sm:items-center sm:justify-between">
                <div><p className="text-sm font-medium text-navy-900">{rf.issue}</p><p className="text-xs text-[var(--wechr-muted-text)]">{rf.category}</p></div>
                <RiskBadge rating={rf.risk_rating} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {review.corrective_actions?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><Wrench className="h-4 w-4 text-wgreen-600" /> Suggested Corrective Actions</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {review.corrective_actions.map((a, i) => (
                <li key={i} className="flex gap-3 text-sm text-navy-800">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-wgreen-100 text-xs font-semibold text-wgreen-700">{i + 1}</span>
                  <span className="pt-0.5">{a}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScorecardView;
